import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ImageUploader } from "./ImageUploader";

interface EditShopItemDialogProps {
  item: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  categories: any[];
}

export function EditShopItemDialog({ item, open, onOpenChange, onSuccess, categories }: EditShopItemDialogProps) {
  const [formData, setFormData] = useState({
    name: item.name,
    description: item.description || "",
    category: item.category,
    images: item.images || [],
    price: item.price || "",
    contact_method: item.contact_method || ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updateData: any = {
      name: formData.name,
      description: formData.description,
      category: formData.category,
      images: formData.images,
      contact_method: formData.contact_method
    };

    if (formData.price) {
      updateData.price = parseFloat(formData.price as any);
    }

    const { error } = await supabase
      .from('shop_items')
      .update(updateData)
      .eq('id', item.id);

    if (error) {
      toast({
        title: "Error updating item",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Success",
        description: "Shop item updated!"
      });
      onOpenChange(false);
      onSuccess();
    }
  };

  const handleImagesChange = (urls: string[]) => {
    setFormData({ ...formData, images: urls });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Shop Item</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              maxLength={100}
            />
          </div>
          <div>
            <Label>Price (£)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              placeholder="Optional"
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              maxLength={1000}
            />
          </div>
          <div>
            <Label>Category</Label>
            <Select
              value={formData.category}
              onValueChange={(value) => setFormData({ ...formData, category: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Contact Method</Label>
            <Input
              value={formData.contact_method}
              onChange={(e) => setFormData({ ...formData, contact_method: e.target.value })}
              placeholder="e.g., Email or Phone"
            />
          </div>
          <div>
            <Label>Images (up to 3)</Label>
            <ImageUploader
              ptaId={item.pta_id}
              itemId={item.id}
              images={formData.images}
              onChange={handleImagesChange}
              maxImages={3}
            />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1">
              Save Changes
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
